// src/pages/OffPageSEO.js
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const OffPageSEO = ({ name }) => {
  const [showScroll, setShowScroll] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    // Scroll to top on mount or when name changes
    window.scrollTo(0, 0);
  }, [name]);

  useEffect(() => {
    // Initialize WOW.js for animations
    if (typeof window !== 'undefined' && window.WOW) {
      new window.WOW().init();
    }

    // Google Analytics
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      function gtag() {
        window.dataLayer.push(arguments);
      }
      gtag('js', new Date());
      gtag('config', 'G-P2TS4436R5');
    }
  }, []);

  // Scroll progress + back-to-top visibility
  useEffect(() => {
    const handleScrollProgress = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress =
        scrollHeight > 0 ? (scrollTop / scrollHeight) * 307.919 : 0;

      setScrollProgress(progress);
      setShowScroll(scrollTop > 300);
    };

    window.addEventListener('scroll', handleScrollProgress);
    handleScrollProgress();

    return () => window.removeEventListener('scroll', handleScrollProgress);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80; // Adjust for fixed navbar height
      const elementPosition =
        element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth',
      });
    }
  };

  const scrollToTop = () =>
    window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Off-Page SEO Strategies to Boost Trust & Google Rankings</title>
        <meta
          name="description"
          content="Learn Off-Page SEO strategies to build trust, boost authority and improve Google rankings with proven techniques for 2025 success."
        />
        <link
          rel="canonical"
          href="https://www.securxperts.com/ai-digital-marketing"
        />
      </Helmet>

      {/* Navbar */}
      <Navbar />

      {/* Hero Section with Banner Image */}
      <header className="relative w-full">
        <div
          className="relative w-full h-64 sm:h-80 md:h-96 lg:h-[420px] bg-center bg-cover bg-no-repeat"
          style={{ backgroundImage: "url('/img/seo.jpg')" }}
        >
          {/* Dark overlay */}
          <div className="absolute inset-0  bg-opacity-60" />

          {/* Text content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white px-4">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-center">
              Off-Page SEO Strategies to Boost Trust & Google Rankings
            </h1>
            <div className="flex items-center gap-2 text-sm sm:text-base">
              <Link to="/" className="hover:underline">
                Home
              </Link>
              <svg
                width="7"
                height="10"
                viewBox="0 0 7 10"
                fill="none"
                className="mt-1"
              >
                <path
                  d="M0.297 0.262C0.105 0.426 -0.002 0.648 0 0.878C0.002 1.109 0.113 1.329 0.308 1.492L4.02 4.604L0.366 7.711C0.173 7.875 0.067 8.097 0.069 8.328C0.071 8.558 0.182 8.778 0.377 8.941C0.474 9.022 0.59 9.086 0.717 9.13C0.844 9.174 0.981 9.196 1.118 9.195C1.255 9.194 1.391 9.171 1.518 9.126C1.644 9.081 1.758 9.016 1.854 8.933L6.232 5.212C6.329 5.131 6.405 5.034 6.457 4.927C6.509 4.821 6.535 4.706 6.534 4.591C6.533 4.476 6.505 4.362 6.451 4.256C6.397 4.15 6.319 4.054 6.221 3.974L1.774 0.254C1.677 0.173 1.561 0.109 1.434 0.065C1.307 0.021 1.171 -0.001 1.033 0C0.896 0.001 0.76 0.024 0.634 0.069C0.507 0.114 0.393 0.18 0.297 0.262Z"
                  fill="white"
                />
              </svg>
              <span>Off-Page SEO Strategies</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content with Sidebar */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-16 py-16 lg:py-24 bg-gradient-to-b from-gray-50 to-gray-100 rounded-3xl shadow-md">
        <div className="flex flex-col lg:flex-row lg:gap-12">
          {/* Main Content */}
          <article className="lg:w-3/4 space-y-12">
            {/* 1. What is Off-Page SEO */}
            <section
              id="what-is-offpage"
              className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-xl transition-all duration-300 animate-slideUp"
              data-wow-delay="0.2s"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 mb-4">
                1. What Is Off-Page SEO and Why Does It Matter?
              </h2>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                When we talk about Off-Page SEO, we mean everything you do
                outside your website to show Google that your site is
                trustworthy and valuable. It’s not just about building
                backlinks; it also includes brand mentions, social shares and
                positive reviews. These signals help search engines understand
                that your website is credible and respected, thereby boosting
                your rankings.
              </p>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mt-3">
                In simple terms, On-Page SEO makes your content great, but
                Off-Page SEO makes the world notice it.
              </p>

              {/* Image Placeholder – What is Off-Page SEO */}
              <div className="image-placeholder mt-6 flex justify-center">
                <img
                  src="/img/blog31.jpg"
                  alt="Overview of Off-Page SEO improving trust and rankings"
                />
              </div>
            </section>

            {/* 2. Why Trust Matters */}
            <section
              id="why-trust"
              className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-xl transition-all duration-300 animate-slideUp"
              data-wow-delay="0.25s"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 mb-4">
                2. Why Trust Matters in Google Rankings
              </h2>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                Google’s primary goal is to give users the most trustworthy and
                helpful results. Websites that appear credible, popular and
                consistently referenced are more likely to rank higher. Trust
                signals show Google that your site provides real value, which
                helps boost your visibility and attract more organic traffic.
              </p>

              {/* Image Placeholder – Trust & Rankings */}
              <div className="image-placeholder mt-6 flex justify-center">
                <img
                  src="/img/blog32.jpg"
                  alt="Trust signals influencing Google rankings"
                />
              </div>
            </section>

            {/* 3. How Off-Page SEO Builds Trust */}
            <section
              id="how-builds-trust"
              className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-xl transition-all duration-300 animate-slideUp"
              data-wow-delay="0.3s"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 mb-4">
                3. How Off-Page SEO Builds Digital Trust
              </h2>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-3">
                Off-Page SEO helps Google and users understand how trustworthy
                your website is by evaluating how others interact with it across
                the internet. When your brand is consistently mentioned,
                reviewed and linked from genuine sources, it builds a strong
                online reputation.
              </p>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                These external signals show that real people and credible
                platforms value your content, which increases your domain
                authority and ranking potential.
              </p>

              <ul className="list-disc pl-6 text-base sm:text-lg text-gray-700 mt-4 space-y-3">
                <li>
                  <strong>Quality Backlinks from Reputed Websites:</strong>{' '}
                  Earning links from trusted websites improves credibility and
                  shows Google your content is valuable.
                </li>
                <li>
                  <strong>Mentions on Blogs and Forums:</strong> Being
                  referenced on blogs, news sites or forums, even without a
                  link, signals your brand is recognized in your industry.
                </li>
                <li>
                  <strong>Positive Reviews and Ratings:</strong> Genuine
                  feedback from real users builds trust and reassures both
                  visitors and search engines.
                </li>
                <li>
                  <strong>Active Social Media Engagement:</strong> Sharing
                  content, replying to comments and interacting with followers
                  demonstrates that your brand is real and reliable.
                </li>
                <li>
                  <strong>Collaborations with Experts or Influencers:</strong>{' '}
                  Partnering with respected industry figures increases authority
                  and enhances your brand’s reputation.
                </li>
                <li>
                  <strong>Consistency Across All Platforms:</strong> Keeping
                  your business name, contact info and profiles uniform across
                  the web shows Google and users that your brand is legitimate.
                </li>
              </ul>

              {/* Image Placeholder – Trust Building */}
              <div className="image-placeholder mt-6 flex justify-center">
                <img
                  src="/img/blog33.jpg"
                  alt="Off-Page SEO activities that build digital trust"
                />
              </div>
            </section>

            {/* 4. Common Mistakes */}
            <section
              id="mistakes"
              className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-xl transition-all duration-300 animate-slideUp"
              data-wow-delay="0.35s"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 mb-4">
                4. Common Off-Page SEO Mistakes to Avoid
              </h2>
              <ul className="list-disc pl-6 text-base sm:text-lg text-gray-700 space-y-3">
                <li>
                  Many websites buy low-quality backlinks, but this often leads
                  to Google penalties and loss of credibility.
                </li>
                <li>
                  Ignoring customer reviews or not responding to feedback makes
                  your brand appear inactive and unreliable.
                </li>
                <li>
                  Having inconsistent business information across platforms
                  confuses both users and search engines.
                </li>
                <li>
                  Neglecting your social media presence weakens brand signals
                  and limits organic engagement.
                </li>
                <li>
                  Focusing only on backlinks while ignoring brand mentions,
                  reviews and engagement prevents long-term growth.
                </li>
              </ul>

              {/* Image Placeholder – Mistakes */}
              <div className="image-placeholder mt-6 flex justify-center">
                <img
                  src="/img/blog34.jpg"
                  alt="Common Off-Page SEO mistakes to avoid"
                />
              </div>
            </section>

            {/* 5. Secur Xperts */}
            <section
              id="securxperts"
              className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-xl transition-all duration-300 animate-slideUp"
              data-wow-delay="0.4s"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 mb-4">
                5. Secur Xperts: Your Partner for Off-Page SEO Success
              </h2>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-3">
                Secur Xperts offers professional Off-Page SEO services,
                including backlinks, brand mentions and social engagement to
                boost website trust. They also provide comprehensive digital
                marketing solutions, including SEO, Google Ads, Meta Ads and
                content marketing, to grow your online presence.
              </p>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                Partnering with them helps you achieve higher rankings and
                long-term credibility for your website by combining strategic
                Off-Page SEO with a strong overall marketing approach.
              </p>

              {/* Image Placeholder – Secur Xperts */}
              <div className="image-placeholder mt-6 flex justify-center">
                <img
                  src="/img/blog35.jpg"
                  alt="Secur Xperts Off-Page SEO and digital marketing services"
                />
              </div>
            </section>

            {/* 6. Conclusion */}
            <section
              id="conclusion"
              className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-xl transition-all duration-300 animate-slideUp"
              data-wow-delay="0.45s"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 mb-4">
                6. Conclusion
              </h2>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-3">
                Off-Page SEO plays a significant role in demonstrating to Google
                and users that your website is trustworthy and valuable. It’s
                not just about getting backlinks; it’s about building your
                online reputation through mentions, reviews and engagement.
              </p>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-3">
                When people talk about your brand positively across different
                platforms, Google starts seeing your website as reliable. By
                focusing on quality, consistency and genuine connections, you
                can improve your rankings and earn long-term trust online.
              </p>
            </section>

            {/* FAQs */}
            <section
              id="faqs"
              className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 bg-gradient-to-br from-gray-50 to-gray-100 hover:shadow-xl transition-all duration-300 animate-slideUp"
              data-wow-delay="0.5s"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-blue-600 mb-4">
                FAQ’s – Off-Page SEO
              </h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl sm:text-2xl font-semibold text-gray-800">
                    1. What is Off-Page SEO?
                  </h3>
                  <p className="text-base sm:text-lg text-gray-700 mt-2 leading-relaxed">
                    Off-Page SEO refers to actions taken outside your website,
                    like link building, guest posting and social media
                    promotion, to improve search rankings and site authority.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-semibold text-gray-800">
                    2. What are the best Off-Page SEO techniques?
                  </h3>
                  <p className="text-base sm:text-lg text-gray-700 mt-2 leading-relaxed">
                    Top techniques include backlink building, social
                    bookmarking, guest blogging, influencer outreach and content
                    sharing on forums or communities.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-semibold text-gray-800">
                    3. How many types of Off-Page SEO are there?
                  </h3>
                  <p className="text-base sm:text-lg text-gray-700 mt-2 leading-relaxed">
                    There are three main types: link building, brand mentions
                    and content marketing.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-semibold text-gray-800">
                    4. Why is Off-Page SEO important?
                  </h3>
                  <p className="text-base sm:text-lg text-gray-700 mt-2 leading-relaxed">
                    It builds trust, improves domain authority and helps your
                    website rank higher in Google search results.
                  </p>
                </div>
              </div>
            </section>
          </article>

          {/* Sidebar */}
          <aside className="lg:w-1/4 mt-12 lg:mt-0">
            <div className="sticky top-24 bg-white rounded-2xl shadow-lg p-8 bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200">
              <h3 className="text-xl font-semibold text-gray-800 mb-6">
                Quick Navigation
              </h3>
              <ul className="space-y-4">
                {[
                  { id: 'what-is-offpage', label: 'What Is Off-Page SEO?' },
                  { id: 'why-trust', label: 'Why Trust Matters' },
                  { id: 'how-builds-trust', label: 'How It Builds Trust' },
                  { id: 'mistakes', label: 'Common Mistakes' },
                  { id: 'securxperts', label: 'Secur Xperts' },
                  { id: 'conclusion', label: 'Conclusion' },
                  { id: 'faqs', label: 'FAQ’s' },
                ].map((item, index) => (
                  <li
                    key={item.id}
                    className="animate-slideInRight"
                    data-wow-delay={`${0.1 * index}s`}
                  >
                    <button
                      onClick={() => scrollToSection(item.id)}
                      className="text-blue-600 hover:text-blue-800 transition-colors text-base font-medium hover:bg-blue-100 rounded-md px-3 py-2 w-full text-left hover:shadow-md"
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>

      {/* Footer */}
      <Footer />

      {/* Scroll to Top with Progress Ring */}
      {showScroll && (
        <div
          onClick={scrollToTop}
          className="scroll-top fixed bottom-6 right-6 z-50 cursor-pointer flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-r from-blue-500 to-blue-700 shadow-lg hover:from-blue-600 hover:to-blue-800 transition-all"
        >
          <svg
            className="progress-circle -rotate-90 w-12 h-12 absolute"
            viewBox="-1 -1 102 102"
          >
            <path
              d="M50,1 a49,49 0 0,1 0,98 a49,49 0 0,1 0,-98"
              fill="none"
              stroke="white"
              strokeWidth="4"
              strokeDasharray="307.919"
              strokeDashoffset={307.919 - scrollProgress}
              style={{ transition: 'stroke-dashoffset 0.1s linear' }}
            />
          </svg>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-6 h-6 text-white relative z-10"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        </div>
      )}

      <style jsx>{`
        @keyframes slideUp {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideInRight {
          0% {
            opacity: 0;
            transform: translateX(20px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .image-placeholder {
          margin: 20px 0;
          display: flex;
          justify-content: center;
        }
        .image-placeholder img {
          max-width: 500px;
          height: auto;
          object-fit: cover;
          border-radius: 10px;
        }
        @media (max-width: 768px) {
          .image-placeholder img {
            max-width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default OffPageSEO;
